import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { createMRTColumnHelper, type MRT_TableOptions } from "material-react-table";
import RowActions from "_shared/_views/tables/row-actions";
import { MRTNoMemo } from "_shared/_views/tables/table";
import {
  BulkInvitationColumnSpec,
  BulkInvitationRowValues,
  createEmptyRow,
  validateBulkInvitationRows,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";
import { BulkInvitationResultRow } from "legal-entities/invitations/bulk/_api/bulk-invitation-api-service";
import BulkInvitationResults from "legal-entities/invitations/bulk/_views/bulk-invitation-results";
import BulkInvitationRowEditor from "legal-entities/invitations/bulk/_views/bulk-invitation-row-editor";
import { RosterSheetError, downloadRosterTemplate, parseRosterWorkbook } from "legal-entities/invitations/bulk/roster-workbook";
import { useRef, useState } from "react";

/**
 * A row as the table holds it. The values are nested rather than spread alongside `rowIndex`,
 * because a row is an open map of cells and a sibling key would be one a spec could collide with.
 */
type GridRow = {
  readonly rowIndex: number;
  readonly values: BulkInvitationRowValues;
};

export type BulkInvitationGridProps = {
  readonly spec: BulkInvitationColumnSpec;

  /**
   * Sends the roster and answers for every row. Rejected rows stay in the grid; invited rows are
   * cleared, so a second attempt is what is left rather than the whole sheet again.
   */
  readonly onSubmit: (rows: readonly BulkInvitationRowValues[]) => Promise<readonly BulkInvitationResultRow[]>;

  readonly submitLabel: string;

  /** Shown above the grid: what this list is for, in the caller's words. */
  readonly description?: string;
};

const columnHelper = createMRTColumnHelper<GridRow>();

/**
 * Hoisted out of the component because it awaits inside a try, which the React Compiler will not
 * lower - a module-level function keeps the control flow exactly as written and costs the grid no
 * memoization. See app/CLAUDE.md, React Compiler.
 */
async function loadSheet(
  file: File,
  spec: BulkInvitationColumnSpec,
  setRows: (rows: readonly BulkInvitationRowValues[]) => void,
  setSheetError: (message: string | null) => void,
  setBusy: (busy: boolean) => void,
) {
  setBusy(true);
  setSheetError(null);
  try {
    const parsed = await parseRosterWorkbook(file, spec);
    setRows(parsed);
  } catch (error) {
    const message =
      error instanceof RosterSheetError
        ? error.userFriendlyMessage
        : "This file could not be read. Download the template and fill that in.";
    setSheetError(message);
  }
  setBusy(false);
}

async function sendRoster(
  rows: readonly BulkInvitationRowValues[],
  onSubmit: BulkInvitationGridProps["onSubmit"],
  setRows: (rows: readonly BulkInvitationRowValues[]) => void,
  setResults: (results: readonly BulkInvitationResultRow[] | null) => void,
  setSheetError: (message: string | null) => void,
  setBusy: (busy: boolean) => void,
) {
  setBusy(true);
  setSheetError(null);
  try {
    const results = await onSubmit(rows);
    setResults(results);

    const keep = new Set<number>();
    for (const result of results) {
      if (result.outcome === "rejected") keep.add(result.index);
    }
    setRows(rows.filter((_row, index) => keep.has(index)));
  } catch {
    setSheetError("Nobody was invited: the request did not reach Attorney Hub. Try again.");
  }
  setBusy(false);
}

export default function BulkInvitationGrid(props: Readonly<BulkInvitationGridProps>) {
  const [rows, setRows] = useState<readonly BulkInvitationRowValues[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [results, setResults] = useState<readonly BulkInvitationResultRow[] | null>(null);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const problems = validateBulkInvitationRows(props.spec, rows);
  const canSend = rows.length > 0 && problems.size === 0 && !busy;

  function replaceRows(next: readonly BulkInvitationRowValues[]) {
    setRows(next);
    // A new list makes the last run's summary describe rows nobody is looking at any more.
    setResults(null);
  }

  function addRow() {
    setEditingIndex(rows.length);
  }

  function deleteRow(index: number) {
    replaceRows(rows.filter((_row, position) => position !== index));
  }

  function saveRow(row: BulkInvitationRowValues) {
    const index = editingIndex ?? 0;
    const next = index >= rows.length ? [...rows, row] : rows.map((existing, position) => (position === index ? row : existing));
    replaceRows(next);
    setEditingIndex(null);
  }

  function pickFile() {
    fileInput.current?.click();
  }

  // Declared above the JSX that calls them. A function declaration after the component's `return`
  // is unreachable code carrying a hoisted binding, which the compiler will not lower - and the
  // whole component then renders unmemoized, silently. See app/CLAUDE.md, React Compiler.
  function replaceRowsAfterSend(next: readonly BulkInvitationRowValues[]) {
    // Deliberately not `replaceRows`: the summary the send just produced is the one thing that
    // should survive the rows changing underneath it.
    setRows(next);
  }

  function send() {
    void sendRoster(rows, props.onSubmit, replaceRowsAfterSend, setResults, setSheetError, setBusy);
  }

  function onFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so picking the same file twice fires a change event the second time.
    event.target.value = "";
    if (file) void loadSheet(file, props.spec, replaceRows, setSheetError, setBusy);
  }

  const columns = [
    columnHelper.accessor((row) => describeProblems(problems.get(row.rowIndex)), {
      id: "problem",
      header: "Problem",
      size: 220,
      Cell: ({ cell }) => {
        const text = cell.getValue<string>();
        if (!text) return null;
        return (
          <Typography component="span" color="error" variant="body2">
            {text}
          </Typography>
        );
      },
    }),
    ...props.spec.fields.map((field) =>
      columnHelper.accessor((row) => renderCellValue(row.values[field.key], field.options), {
        id: field.key,
        header: field.header,
      }),
    ),
  ];

  const tableOptions: MRT_TableOptions<GridRow> = {
    columns,
    data: rows.map((values, rowIndex) => ({ rowIndex, values })),
    enableSorting: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    // Matching permissions-matrix-table: the row is edited in a form of its own, not in place.
    editDisplayMode: "custom",
    enableEditing: true,
    muiTableBodyRowProps: ({ row }) => ({
      sx: problems.has(row.original.rowIndex) ? { backgroundColor: "error.light", opacity: 0.85 } : undefined,
    }),
    renderEmptyRowsFallback: () => (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2">
          {`No ${props.spec.noun}s yet. Add one, or upload a filled-in template.`}
        </Typography>
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <RowActions
        actions={[
          {
            key: "edit",
            label: "Edit",
            ariaLabel: `Edit row ${row.original.rowIndex + 1}`,
            icon: <EditIcon />,
            onClick: () => setEditingIndex(row.original.rowIndex),
          },
          {
            key: "delete",
            label: "Remove",
            ariaLabel: `Remove row ${row.original.rowIndex + 1}`,
            icon: <DeleteIcon />,
            destructive: true,
            onClick: () => deleteRow(row.original.rowIndex),
          },
        ]}
      />
    ),
  };

  return (
    <Stack spacing={2}>
      {props.description ? <Typography variant="body2">{props.description}</Typography> : null}

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Button startIcon={<AddIcon />} onClick={addRow} disabled={busy}>
          {`Add ${props.spec.noun}`}
        </Button>
        <Button startIcon={<DownloadIcon />} onClick={() => void downloadRosterTemplate(props.spec)} disabled={busy}>
          Download template
        </Button>
        <Button startIcon={<UploadFileIcon />} onClick={pickFile} disabled={busy}>
          Upload filled-in template
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={sendDisabledReason(rows.length, problems.size)}>
          <span>
            <Button variant="contained" onClick={send} disabled={!canSend}>
              {props.submitLabel}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.xls,.csv"
        aria-label="Upload filled-in template"
        style={{ display: "none" }}
        onChange={onFileChosen}
      />

      {sheetError ? <Alert severity="error">{sheetError}</Alert> : null}
      {results ? <BulkInvitationResults results={results} noun={props.spec.noun} /> : null}

      <MRTNoMemo options={tableOptions} />

      {editingIndex === null ? null : (
        <BulkInvitationRowEditor
          key={editingIndex}
          spec={props.spec}
          open
          title={editingIndex >= rows.length ? `Add ${props.spec.noun}` : `Edit ${props.spec.noun}`}
          row={editingIndex >= rows.length ? createEmptyRow(props.spec) : rows[editingIndex]}
          precedingRows={rows.slice(0, editingIndex)}
          onCancel={() => setEditingIndex(null)}
          onSave={saveRow}
        />
      )}
    </Stack>
  );
}

function describeProblems(problems: readonly { message: string }[] | undefined): string {
  if (!problems || problems.length === 0) return "";
  return problems.map((problem) => problem.message).join(" ");
}

function renderCellValue(
  value: BulkInvitationRowValues[string],
  options: readonly { value: string; label: string }[] | undefined,
): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (!value) return "";
  const option = options?.find((candidate) => candidate.value === value);
  return option ? option.label : value;
}

function sendDisabledReason(rowCount: number, problemCount: number): string {
  if (rowCount === 0) return "Add at least one row first.";
  if (problemCount > 0) {
    return problemCount === 1 ? "One row still needs fixing." : `${problemCount} rows still need fixing.`;
  }
  return "";
}
